import React, { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle, Trash2, X } from 'lucide-react'
import type { Reservation } from '@/lib/pos-types'

interface CancelReservationModalProps {
  reservation: Reservation | null
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void> | void
  isSubmitting?: boolean
  disabled?: boolean
}

export const CancelReservationModal: React.FC<CancelReservationModalProps> = ({
  reservation,
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  disabled = false,
}) => {
  // Cerrar al presionar la tecla Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isSubmitting, onClose])

  if (!isOpen || !reservation) return null

  const tableText = reservation.tableNumber ? `Mesa ${reservation.tableNumber}` : 'Reserva Sin Mesa'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-[1px] animate-in fade-in duration-150">
      {/* Cierre al hacer clic fuera de la tarjeta */}
      <div className="absolute inset-0" onClick={() => { if (!isSubmitting) onClose() }} />

      {/* Tarjeta Flotante Sin Fondo Negro Oscurecedor */}
      <div className="relative z-10 w-full max-w-md bg-card/95 backdrop-blur-md border border-border/80 rounded-xl p-5 shadow-2xl shadow-black/90 space-y-4 animate-in fade-in zoom-in-95 duration-150">
        {/* Encabezado con Boton X */}
        <div className="flex items-center justify-between border-b border-border/40 pb-3">
          <div className="flex items-center gap-2 text-destructive font-bold text-lg">
            <Trash2 className="h-5 w-5 shrink-0" />
            <span>Anular Reserva</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md transition-colors"
            title="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground">
          ¿Estás seguro de que deseas anular esta reserva? Esta acción no se puede deshacer.
        </p>

        {/* Card Informativa de la Reserva */}
        <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-sm text-foreground">
              {reservation.reserverName}
            </span>
            <Badge
              variant="outline"
              className={
                reservation.tableNumber
                  ? 'bg-primary/10 text-primary border-primary/20 font-semibold'
                  : 'bg-amber-500/10 text-amber-500 border-amber-500/20 font-semibold'
              }
            >
              {tableText}
            </Badge>
          </div>

          <div className="text-xs text-muted-foreground grid grid-cols-2 gap-1 pt-1 border-t border-border/50">
            <div>
              <span className="text-muted-foreground/70">Comensales:</span>{' '}
              <strong className="text-foreground">{reservation.partySize} personas</strong>
            </div>
            <div>
              <span className="text-muted-foreground/70">Horario:</span>{' '}
              <strong className="text-foreground">
                {reservation.reservedAt ? new Date(reservation.reservedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
              </strong>
            </div>
          </div>

          {reservation.notes && (
            <div className="text-xs text-muted-foreground bg-background/60 p-2 rounded border border-border/40 italic">
              "{reservation.notes}"
            </div>
          )}
        </div>

        {/* Advertencia de Eliminacion de Comanda */}
        <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 text-destructive text-xs p-2.5 rounded-lg">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Al anular la reserva, la comanda asociada y sus ítems de cocina serán eliminados automáticamente.
          </span>
        </div>

        {/* Botones de Accion */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            size="sm"
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onConfirm()}
            disabled={disabled || isSubmitting}
            size="sm"
            className="gap-1.5"
          >
            <Trash2 className="h-4 w-4" />
            {isSubmitting ? 'Anulando...' : 'Confirmar Anulación'}
          </Button>
        </div>
      </div>
    </div>
  )
}
